<template>
    <v-card>
        <v-toolbar color="primary" density="compact">
            <v-toolbar-title>{{ $t("company.cardFleetTitle") }}</v-toolbar-title>
            <v-spacer />
            <v-tooltip v-if="canEdit && !isEditMode" location="bottom">
                <template #activator="{ props }">
                    <v-btn
                        v-bind="props"
                        icon
                        variant="text"
                        color="yellow"
                        elevation="0"
                        @click="enterEditMode"
                    >
                        <v-icon size="small">mdi-pencil</v-icon>
                    </v-btn>
                </template>
                {{ $t("company.fleetEditTooltip") }}
            </v-tooltip>
            <v-tooltip v-if="canEdit && isEditMode" location="bottom">
                <template #activator="{ props }">
                    <v-btn
                        v-bind="props"
                        icon
                        variant="text"
                        color="red"
                        elevation="0"
                        @click="exitEditMode"
                    >
                        <v-icon size="small">mdi-close</v-icon>
                    </v-btn>
                </template>
                {{ $t("company.fleetCancelEditTooltip") }}
            </v-tooltip>
        </v-toolbar>
        <v-card-text>
            <div v-auto-animate>
                <!-- View mode: read-only list -->
                <template v-if="!isEditMode">
                <v-row v-if="draft.length" dense>
                    <v-col
                        v-for="(item, index) in draft"
                        :key="index"
                        cols="12"
                        sm="6"
                        md="6"
                        lg="6"
                        class="py-1"
                    >
                        <div class="d-flex text-body-1 text-nowrap">
                            <span class="me-2" style="min-width: 100px">{{ truckTypeLabel(item.truck_type) }}</span>
                            <span class="me-2">{{ truckCoatingLabel(item.truck_coating) }}</span>
                            <span>× {{ item.count }}</span>
                        </div>
                    </v-col>
                </v-row>
                <p v-else class="text-body-1">
                    {{ $t("company.fleetEmpty") }}
                </p>
            </template>

            <!-- Edit mode: add row + editable list + save -->
            <template v-else>
                <v-row>
                    <v-col cols="12" sm="6" md="3">
                        <FleetTruckSelect
                            v-model="newTruckType"
                            :items="truckTypeOptions"
                            :label="$t('company.fleetTruckType')"
                            hide-details="auto"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-select
                            v-model="newTruckCoating"
                            :items="truckCoatingOptions"
                            item-title="text"
                            item-value="value"
                            :label="$t('company.fleetCoating')"
                            hide-details="auto"
                            density="compact"
                            variant="outlined"
                        />
                    </v-col>
                    <v-col cols="12" sm="4" md="2">
                        <v-text-field
                            v-model.number="newCount"
                            type="number"
                            min="1"
                            :label="$t('company.fleetCount')"
                            hide-details="auto"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="12" sm="4" md="2">
                        <v-btn
                            color="primary"
                            variant="flat"
                            :disabled="!canAddNew"
                            @click="addToDraft"
                        >
                            {{ $t("company.fleetAdd") }}
                        </v-btn>
                    </v-col>
                </v-row>

                <template v-if="draft.length">
                    <v-row
                        v-for="(item, index) in draft"
                        :key="index"
                    >
                        <v-col cols="12" sm="6" md="3">
                            <FleetTruckSelect
                                :model-value="item.truck_type"
                                :items="truckTypeOptions"
                                hide-details
                                @update:model-value="updateDraftItem(index, 'truck_type', $event)"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                            <v-select
                                :model-value="item.truck_coating"
                                :items="truckCoatingOptions"
                                item-title="text"
                                item-value="value"
                                :label="$t('company.fleetCoating')"
                                hide-details
                                density="compact"
                                variant="outlined"
                                @update:model-value="updateDraftItem(index, 'truck_coating', $event)"
                            />
                        </v-col>
                        <v-col cols="12" sm="4" md="2">
                            <v-text-field
                                :model-value="item.count"
                                type="number"
                                min="1"
                                hide-details
                                density="compact"
                                @update:model-value="updateDraftItem(index, 'count', $event)"
                            />
                        </v-col>
                        <v-col cols="12" sm="4" md="2">
                            <v-btn
                                size="small"
                                icon
                                color="error"
                                @click="removeFromDraft(index)"
                            >
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                </template>
                <p v-else class="text-medium-emphasis text-body-2">
                    {{ $t("company.fleetEmpty") }}
                </p>

                <v-btn
                    class="mt-3"
                    color="primary"
                    variant="flat"
                    :loading="saving"
                    :disabled="!hasUnsavedChanges"
                    @click="saveFleet"
                >
                    {{ $t("company.fleetSave") }}
                </v-btn>
            </template>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
import axios from "axios";
import { getTruckTypesInDisplayOrder } from "@/enums/truckEnums.js";
import { showAlertModal } from "@/utils";
import FleetTruckSelect from "./FleetTruckSelect.vue";

export default {
    name: "FleetEditor",
    components: { FleetTruckSelect },
    props: {
        fleet: {
            type: Array,
            default: () => [],
        },
        companyId: {
            type: [Number, String],
            required: true,
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["saved"],
    data() {
        return {
            draft: [],
            isEditMode: false,
            newTruckType: null,
            newTruckCoating: 1,
            newCount: null,
            saving: false,
        };
    },
    computed: {
        truckTypeOptions() {
            return getTruckTypesInDisplayOrder().map((item) => ({
                value: item.value,
                text: this.$t(`transport.truckType.${item.value}`) || item.text,
            }));
        },
        truckCoatingOptions() {
            return [1, 2].map((value) => ({
                value,
                text: this.$t(`transport.truckCoating.${value}`) || String(value),
            }));
        },
        canAddNew() {
            return this.newTruckType != null && this.newCount >= 1 && this.newTruckCoating != null;
        },
        hasUnsavedChanges() {
            if (this.draft.length !== this.fleet.length) return true;
            return this.draft.some(
                (item, i) =>
                    item.truck_type !== this.fleet[i].truck_type ||
                    Number(item.truck_coating) !== Number(this.fleet[i].truck_coating ?? 1) ||
                    Number(item.count) !== Number(this.fleet[i].count)
            );
        },
    },
    watch: {
        fleet: {
            handler(val) {
                this.draft = (val || []).map((item) => ({
                    ...item,
                    truck_coating: item.truck_coating != null ? Number(item.truck_coating) : 1,
                }));
            },
            immediate: true,
        },
    },
    methods: {
        truckTypeLabel(truckTypeValue) {
            const opt = this.truckTypeOptions.find((o) => o.value === truckTypeValue);
            return opt?.text ?? truckTypeValue;
        },
        truckCoatingLabel(coatingValue) {
            const v = coatingValue != null ? Number(coatingValue) : 1;
            const opt = this.truckCoatingOptions.find((o) => o.value === v);
            return opt?.text ?? v;
        },
        enterEditMode() {
            this.isEditMode = true;
        },
        exitEditMode() {
            this.isEditMode = false;
            this.draft = (this.fleet || []).map((item) => ({
                ...item,
                truck_coating: item.truck_coating != null ? Number(item.truck_coating) : 1,
            }));
        },
        addToDraft() {
            if (!this.canAddNew) return;
            this.draft.push({
                truck_type: this.newTruckType,
                truck_coating: this.newTruckCoating,
                count: this.newCount,
            });
            this.newTruckType = null;
            this.newTruckCoating = 1;
            this.newCount = null;
        },
        updateDraftItem(index, field, value) {
            const item = { ...this.draft[index] };
            if (field === "count") {
                item.count = Math.max(1, Number(value) || 1);
            } else if (field === "truck_coating") {
                item.truck_coating = value != null ? Number(value) : 1;
            } else {
                item[field] = value;
            }
            this.draft.splice(index, 1, item);
        },
        removeFromDraft(index) {
            this.draft.splice(index, 1);
        },
        async saveFleet() {
            if (!this.hasUnsavedChanges) return;
            this.saving = true;
            try {
                await axios.patch(
                    `${process.env.VUE_APP_API_URL}/companies/${this.companyId}/`,
                    { fleet: this.draft }
                );
                this.isEditMode = false;
                this.$emit("saved");
                showAlertModal(
                    this.$store,
                    this.$t("company.fleetSaveSuccess"),
                    "success",
                    3000
                );
            } catch (error) {
                console.error("Error saving fleet:", error);
                showAlertModal(
                    this.$store,
                    this.$t("company.fleetSaveError"),
                    "danger",
                    8000,
                    error
                );
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
